import * as React from 'react';
import { useTheme } from '@emotion/react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloud, faCode, faDatabase, faGlobe, faShieldHalved, faTerminal } from '@fortawesome/free-solid-svg-icons';
import { FEATURE_SYSTEMS, FEATURE_NODE_LABELS } from '../../../../data/features';
import { STORY_NODES, STORY_SHARED_TOOLS, STORY_TIMELINE, StoryScene } from '../story';
import { CloudCodeTask } from './CloudCodeTask';

interface PlatformSystemsArtworkProps {
  scene: StoryScene;
  connectedTime: number;
  focusApps: boolean;
  grouped: boolean;
  cloudOffset: number;
  reducedMotion: boolean;
  taskTriggered: boolean;
  animateConnections: boolean;
}

export const PlatformSystemsArtwork: React.FC<PlatformSystemsArtworkProps> = ({
  scene,
  connectedTime,
  focusApps,
  grouped,
  cloudOffset,
  reducedMotion,
  taskTriggered,
  animateConnections
}) => {
  const theme = useTheme();
  const colors = theme.featureGraph;
  const connected = scene === StoryScene.CONNECTED;
  const transition = { duration: reducedMotion ? 0 : 0.7 };
  const NODE_ICONS = {
    backend: faDatabase,
    cloudcode: faCloud,
    oxide: faCode,
    cli: faTerminal,
    sector: faDatabase,
    override: faShieldHalved,
    'web-container': faGlobe
  };
  return (
    <>
      {STORY_NODES.map((node) => {
        const system = FEATURE_SYSTEMS.find((item) => item.id === node.system)!;
        const color = colors[system.type];
        const sharedTool = STORY_SHARED_TOOLS.includes(node.system);
        const position = grouped
          ? sharedTool
            ? { x: node.x, y: node.groupedY ?? node.y, scale: 1 }
            : { x: 64 + node.x * 0.25, y: 390 + node.y * 0.25, scale: 0.25 }
          : {
              x: connected && node.system === 'backend' ? 377 : connected && node.system === 'cloudcode' ? 739 : node.x,
              y: node.y + (['backend', 'cloudcode'].includes(node.system) ? cloudOffset : 0),
              scale: 1
            };
        return (
          <motion.g
            key={node.system}
            initial={false}
            animate={{
              opacity:
                focusApps && sharedTool
                  ? 0
                  : connected && node.system === 'backend'
                    ? connectedTime >= STORY_TIMELINE.connected.syncOffsetStartMs
                      ? 1
                      : 0
                    : connected && node.system === 'cloudcode'
                      ? connectedTime >= STORY_TIMELINE.connected.taskOffsetStartMs
                        ? 1
                        : 0
                      : scene >= node.scene
                        ? 1
                        : 0
            }}
            transition={transition}
          >
            <g
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${position.scale})`,
                transformOrigin: '0px 0px',
                transformBox: 'view-box',
                transition: reducedMotion ? 'none' : 'transform 1.6s cubic-bezier(0.22, 1, 0.36, 1)'
              }}
            >
              {node.system === 'cloudcode' ? (
                <CloudCodeTask
                  compact
                  triggered={taskTriggered}
                  title="Generate report"
                  source="Inspection"
                  result="PDF report"
                  running={animateConnections}
                />
              ) : (
                <>
                  <rect
                    width="250"
                    height="140"
                    rx="18"
                    fill={theme.surface.background}
                    stroke={color.border}
                    strokeWidth="1.5"
                  />
                  <rect width="250" height="140" rx="18" fill={color.background} />
                  <FontAwesomeIcon
                    icon={NODE_ICONS[node.system]}
                    x="20"
                    y="21"
                    width="20"
                    height="20"
                    color={color.accent}
                  />
                  <text x="50" y="35" fill={color.accent} fontSize="10" letterSpacing="1.2">
                    {FEATURE_NODE_LABELS[system.type].toUpperCase()}
                  </text>
                  <text x="20" y="75" fill={theme.text.heading} fontSize="21" fontWeight="600">
                    {system.name}
                  </text>
                  <text x="20" y="105" fill={theme.text.paragraph} fontSize="12">
                    {node.detail}
                  </text>
                </>
              )}
            </g>
          </motion.g>
        );
      })}
    </>
  );
};
